// Sentry initialization should be imported first!
import "./sentry";
import * as Sentry from "@sentry/react";
import './polyfills/buffer'
import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { AuthProvider } from './context/auth-context'
import { FontProvider } from './context/font-context'
import { ThemeProvider } from './context/theme-context'
import { MotionConfig } from 'motion/react'
import { MotionConfig as MotionConfigLegacy } from 'framer-motion'
import { Agentation } from 'agentation'
import './index.css'
// import '@deepguide-ai/video-editor/dist/index.css'
import { queryClient } from './lib/trpc'
import { router } from './router'
import { initPostHog, trackEvent } from './lib/posthog'
import { initDevTools } from './utils/dev-tools'

// Reload once when Vite chunk preloads fail (stale deploy).
// IMPORTANT: do NOT call e.preventDefault(). Vite's preload helper swallows the
// error when defaultPrevented, causing import() to resolve with `undefined`
// instead of rejecting. The synchronous `.then((d) => d.Route)` callback in
// routeTree.gen.ts then crashes with `Cannot read properties of undefined
// (reading 'Route')` before the reload navigates away. Letting the error
// propagate makes the import reject cleanly while we still trigger the reload.
window.addEventListener('vite:preloadError', () => {
  const reloadKey = 'vite-preload-reload'
  const ts = sessionStorage.getItem(reloadKey)
  if (!ts || Date.now() - Number(ts) > 10_000) {
    sessionStorage.setItem(reloadKey, String(Date.now()))
    window.location.reload()
  }
})

// Initialize PostHog analytics
initPostHog()

// Initialize dev tools (dev mode only)
initDevTools()


// Optimize app startup
const rootElement = document.getElementById('root')!

// Add antialiasing and mark body as loaded to prevent FOUC
rootElement.classList.add('antialiased')
document.body.classList.add('loaded')

// Create root with optimized error handling
const root = ReactDOM.createRoot(rootElement, import.meta.env.PROD ? {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack);
    // Track error in PostHog
    try {
      trackEvent('error.occurred', {
        errorMessage: error.message,
        errorStack: error.stack,
        componentStack: errorInfo.componentStack,
        errorType: 'uncaught'
      })
    } catch (e) {
      console.error('Failed to track error:', e)
    }
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    // Track caught error in PostHog
    try {
      trackEvent('error.occurred', {
        errorMessage: error.message,
        errorStack: error.stack,
        componentStack: errorInfo?.componentStack,
        errorType: 'caught'
      })
    } catch (e) {
      console.error('Failed to track error:', e)
    }
  }),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler(),
} : undefined)

root.render(
  // <StrictMode>
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <FontProvider>
            {/* reducedMotion="user" makes every Framer Motion component respect
                the OS setting — drops transform/layout motion, keeps opacity.
                Both providers are needed while the app mixes framer-motion@11
                and motion@12 (separate module instances → separate contexts). */}
            <MotionConfigLegacy reducedMotion="user">
              <MotionConfig reducedMotion="user">
                <RouterProvider router={router} />
                {import.meta.env.DEV && <Agentation />}
              </MotionConfig>
            </MotionConfigLegacy>
          </FontProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </AuthProvider>
  // </StrictMode>
)

// After initializing the router, expose a navigation function for the protocol handler
// Only do this in a production Electron environment to avoid security issues in web context
if (window._main_env?.IS_DESKTOP) {
  // Expose a global navigation function that the protocol handler can use
  window._screenata_app_navigate = (path: string): boolean => {
    try {
      router.navigate({ to: path })
      return true
    } catch (_error) {
      try {
        window.location.href = path
        return true
      } catch {
        return false
      }
    }
  }

  console.log('Navigation helper exposed for protocol handler')
}
